/**
 * Dialog/sheet data prefetch helpers.
 *
 * Pair with `usePrefetchOnIntent` on the trigger to warm React Query's cache
 * BEFORE the open animation completes — eliminates the "snap-in" where a
 * tray opens and content pops in 200-500ms later. See
 * mem://platform/no-popin-dialogs-v1.
 */
import type { QueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export const sellerContactNoteKey = (username: string) =>
	['seller-contact-note', username] as const;

export async function fetchSellerContactNote(username: string): Promise<string | null> {
	const { data } = await supabase
		.from('public_profiles')
		.select('contact_instructions')
		.eq('username', username)
		.maybeSingle();
	const note = ((data as any)?.contact_instructions ?? '').trim();
	return note ? note : null;
}

export function prefetchSellerContactNote(qc: QueryClient, username: string | null | undefined) {
	if (!username) return;
	void qc.prefetchQuery({
		queryKey: sellerContactNoteKey(username),
		queryFn: () => fetchSellerContactNote(username),
		staleTime: 5 * 60_000,
	});
}

/**
 * Seed the contact-note cache from a parent query that already loaded the
 * seller's profile row. Zero round-trips. Preferred over prefetchSellerContactNote
 * whenever the surface has already fetched profiles/public_profiles.
 */
export function seedSellerContactNote(
	qc: QueryClient,
	username: string | null | undefined,
	rawNote: string | null | undefined,
) {
	if (!username) return;
	const note = (rawNote ?? '').trim();
	qc.setQueryData(sellerContactNoteKey(username), note ? note : null);
}
